import React, { useEffect, useState } from 'react';

// Shared list view for the financial dashboard pages (Transactions, Deposits,
// Payments). All three back-ends proxy to SaaS RemoteCashiering and may return
// either an array, a { status, result } envelope, or { status:false, message }.

function normalizeRows(res) {
  if (Array.isArray(res)) return res;
  // The SaaS RemoteCashiering proxy nests the array inside `result` under a
  // type key, e.g. { success:true, result:{ transactions:[…] } } or
  // { result:{ deposits:[…] } } (Payments also returns under `deposits`).
  const r = res?.result;
  if (Array.isArray(r)) return r;
  if (r && typeof r === 'object') {
    return r.transactions || r.deposits || r.payments || r.lineItems || [];
  }
  return res?.transactions || res?.deposits || res?.payments || res?.data || [];
}

function formatAmount(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
}

function FinancialList({ userSettings, fetcher, emptyLabel }) {
  const [state, setState] = useState({ status: 'loading', rows: [], message: null });

  useEffect(() => {
    let cancelled = false;
    const driverId =
      userSettings?.Id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;
    if (!driverId) {
      setState({ status: 'error', rows: [], message: 'Driver not identified' });
      return;
    }
    fetcher(driverId)
      .then((res) => {
        if (cancelled) return;
        if (res && res.status === false) {
          setState({ status: 'empty', rows: [], message: res.message || emptyLabel });
          return;
        }
        setState({ status: 'loaded', rows: normalizeRows(res), message: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: 'error', rows: [], message: err?.message || 'Failed to load' });
      });
    return () => {
      cancelled = true;
    };
  }, [userSettings, fetcher, emptyLabel]);

  if (state.status === 'loading') {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-2xl bg-gray-200" />
        ))}
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="rounded-2xl bg-white px-4 py-6 text-center text-sm text-gray-500 shadow-sm">
        {state.message}
      </div>
    );
  }

  if (state.status === 'empty' || !state.rows.length) {
    return (
      <div className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
        {state.message || emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {state.rows.map((row, i) => {
        const when = row.date || row.Date || row.createdOn || row.CreatedOn || '';
        const desc =
          row.description || row.Description || row.type || row.Type || row.note || '';
        const amount = formatAmount(row.amount ?? row.Amount ?? row.total ?? row.Total);
        const balance = formatAmount(row.balance ?? row.Balance);
        return (
          <div key={row.id || row.Id || i} className="rounded-2xl bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{when || `Item ${i + 1}`}</span>
              {amount && <span className="text-sm font-semibold text-gray-900">{amount}</span>}
            </div>
            {desc && <div className="mt-1 text-sm text-gray-800">{desc}</div>}
            {balance && (
              <div className="mt-0.5 text-xs text-gray-500">Balance: {balance}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default FinancialList;
